'use strict';
import sxQuery from '../../sxQuery/sxQuery';
import SxQueryObject from '../../sxQuery/SxQueryObject';
import RebindStrategy from '../interface/RebindStrategy';
import EventHelper from './EventHelper';
import Helper from '../../siteSearch/utils/Helper';
const EVENT_TYPES = ['keydown', 'submit', 'click', 'focus', 'keyup', 'blur', 'keydown', 'beforeunload'];
const removeHiddenItems = (nodeList) => {
    if (nodeList === undefined) {
        return [];
    }
    const result = [];
    nodeList.forEach((node) => {
        const isHidden = window.getComputedStyle(node).display === 'none';
        if (!isHidden) {
            result.push(node);
        }
    });
    return result;
};
class EventBinder {
    constructor(tracker, debugMethod = (_message) => { }) {
        this.tracker = tracker;
        this.instanceId = `insights_${Math.round(Math.random() * 100000)}`;
        this.disableOnUnloadFlag = false;
        this.isSerpAbandonBound = false;
        this.isSerpClickBound = false;
        this.isSerpShownBound = false;
        this.debugMethod = debugMethod;
    }
    clearEvents(node) {
        EVENT_TYPES.forEach((e) => {
            node.off(`${e}.${this.instanceId}`);
        });
    }
    bindSearchBoxEvents(selector, searchButtonSelector, suggestBlockSelector, searchSuggestionSelector, activeSuggestionSelector, articleNumberSource, identifierSource, trackSubmitOnly = false, ignoreForms = false) {
        this.debugMethod('Attaching search box events');
        const $searchBox = sxQuery(selector);
        const $searchButton = sxQuery(searchButtonSelector);
        const $suggestBox = sxQuery(suggestBlockSelector).length > 0 ? sxQuery(sxQuery(suggestBlockSelector).get()[0]) : new SxQueryObject([]);
        this.clearEvents($searchBox);
        this.clearEvents($searchButton);
        // basic search box events
        const focusHandler = (e) => {
            // @ts-ignore
            this.tracker.trackSearchBoxFocus(e.target);
        };
        const changeHandler = (e) => {
            // @ts-ignore
            this.tracker.collectSearchBoxChange(e.target);
        };
        const blurHandler = (e) => {
            const suggestCount = searchSuggestionSelector ? $suggestBox.find(searchSuggestionSelector).length : undefined;
            // @ts-ignore
            this.tracker.trackSearchBoxAbandon(sxQuery(e.target).val() || $searchBox.val(), suggestCount, e.target);
        };
        // search suggestion events
        let activeSuggestionCache = new SxQueryObject([]);
        const suggestSelectHandler = suggestBlockSelector ? (e) => {
            let selectedSuggestion = $suggestBox.find(activeSuggestionSelector);
            if (e.keyCode >= 37 && e.keyCode <= 40) {
                activeSuggestionCache = selectedSuggestion;
            }
            else if (e.keyCode !== 13) {
                activeSuggestionCache = new SxQueryObject([]);
            }
            if (e.keyCode === 13 && (activeSuggestionCache.length > 0 || selectedSuggestion.length > 0)) {
                selectedSuggestion = selectedSuggestion.length > 0 ? selectedSuggestion : activeSuggestionCache;
                const allSuggestions = $suggestBox.find(searchSuggestionSelector);
                const link = selectedSuggestion.find('a[href]').length > 0 ? selectedSuggestion.find('a[href]').attr('href') : selectedSuggestion.text();
                // @ts-ignore
                this.tracker.trackSelectSuggest(e.target, sxQuery(suggestBlockSelector).get()[0], selectedSuggestion.get()[0], sxQuery(e.target).val() || $searchBox.val(), allSuggestions.length, Helper.getPositionInNodeList(allSuggestions.get(), selectedSuggestion.get()[0]), link, undefined, undefined, EventHelper.getDataSourceValue(articleNumberSource, selectedSuggestion.get()[0]), EventHelper.getDataSourceValue(identifierSource, selectedSuggestion.get()[0]));
            }
        } : undefined;
        // search submit events
        const submitHandler = (e) => {
            if (e.keyCode === 13) {
                // @ts-ignore
                this.tracker.trackSubmitSearch(sxQuery(e.target).val() || $searchBox.val(), e.target, undefined, undefined);
            }
        };
        const searchButtonHandler = (e) => {
            // @ts-ignore
            this.tracker.trackSubmitSearch($searchBox.val(), $searchBox.get()[0], e.target, undefined);
        };
        const findParentForm = (startNode) => {
            // @ts-ignore
            let parentNode = startNode.parentNode;
            while (parentNode) {
                if (parentNode.tagName === 'FORM' && parentNode.getAttribute('action')) {
                    return parentNode;
                }
                // @ts-ignore
                parentNode = parentNode.parentNode;
            }
            return undefined;
        };
        document.querySelectorAll(selector).forEach((searchBox) => {
            const form = ignoreForms ? undefined : findParentForm(searchBox);
            if (!form) { // no form found
                sxQuery(searchBox).on(`keydown.${this.instanceId}`, submitHandler);
                this.debugMethod('Attached keydown listener to search box', searchBox);
            }
            else {
                const $form = sxQuery(form);
                const formSubmitHandler = () => {
                    // @ts-ignore
                    this.tracker.trackSubmitSearch(searchBox.value, searchBox, undefined, undefined); // we will simply ignore the search button in this case
                };
                this.clearEvents($form);
                $form.on(`submit.${this.instanceId}`, formSubmitHandler);
                this.debugMethod('Attached submit listener to form', form);
            }
        });
        document.querySelectorAll(searchButtonSelector).forEach((searchButton) => {
            const form = ignoreForms ? undefined : findParentForm(searchButton);
            if (!form) {
                sxQuery(searchButton).on(`click.${this.instanceId}`, searchButtonHandler);
                this.debugMethod('Attached click listener to search button', searchButton);
            }
            else {
                // we assume that this is already bound by search box
            }
        });
        if (!trackSubmitOnly) {
            $searchBox.on(`focus.${this.instanceId}`, focusHandler);
            $searchBox.on(`keyup.${this.instanceId}`, changeHandler);
            $searchBox.on(`blur.${this.instanceId}`, blurHandler);
            if (suggestSelectHandler !== undefined) {
                $searchBox.on(`keydown.${this.instanceId}`, suggestSelectHandler);
            }
            this.debugMethod('Attached focus, keyup and blur listeners to search box(es)', $searchBox.get());
        }
    }
    bindSuggestBoxEvents(blockSelector, itemSelector, searchBoxSelector, articleNumberSource, identifierSource, addToCartTrigger, countSource, priceSource, priceUnitSource) {
        // @ts-ignore
        const suggestBox = document.querySelector(blockSelector);
        // @ts-ignore
        const searchBox = document.querySelector(searchBoxSelector);
        if (!suggestBox || !suggestBox.querySelector(itemSelector)) { // no suggest box or no items
            this.debugMethod('No suggest box or items found');
            return;
        }
        const getQuery = () => {
            const searchBoxes = sxQuery(searchBoxSelector).get();
            for (let i = 0; i < searchBoxes.length; i++) {
                const $searchBox = sxQuery(searchBoxes[i]);
                if ($searchBox.val()) {
                    // @ts-ignore
                    return $searchBox.val();
                }
            }
            return undefined;
        };
        if (getComputedStyle(suggestBox).display !== 'none') {
            // @ts-ignore
            this.tracker.trackShowSuggests(searchBox, suggestBox, suggestBox.querySelector(itemSelector), getQuery(), suggestBox.querySelectorAll(itemSelector).length);
        }
        const trackLinksOnly = suggestBox.querySelector(itemSelector).querySelector('a') != null;
        const trackSelectCallback = (selectedItem, selectedLink) => {
            if (!selectedLink) {
                const firstLink = selectedItem.querySelector('a[href]');
                selectedLink = firstLink ? firstLink.getAttribute('href') : selectedItem.innerText;
            }
            const allItems = [].slice.call(suggestBox.querySelectorAll(itemSelector));
            const position = Helper.getPositionInNodeList(allItems, selectedItem);
            // @ts-ignore
            this.tracker.trackSelectSuggest(searchBox, suggestBox, selectedItem, getQuery(), allItems.length, position, selectedLink, undefined, undefined, EventHelper.getDataSourceValue(articleNumberSource, selectedItem), EventHelper.getDataSourceValue(identifierSource, selectedItem));
        };
        const handler = trackLinksOnly ? (e) => {
            const $matchingParents = sxQuery(e.target).parents(blockSelector, true);
            if (e.target && $matchingParents.length > 0) {
                // @ts-ignore
                trackSelectCallback(e.target, sxQuery(e.target).attr('href'));
            }
        } : function (e) {
            // @ts-ignore
            trackSelectCallback(e.target);
        };
        const $targets = trackLinksOnly ? sxQuery(suggestBox).find(itemSelector).find('a') : sxQuery(suggestBox).find(itemSelector);
        this.clearEvents($targets);
        $targets.on(`click.${this.instanceId}`, handler);
        this.debugMethod('Attached click listeners to suggest box items', $targets.get());
        if (addToCartTrigger !== undefined) {
            sxQuery(suggestBox).find(itemSelector).each((item) => {
                const $item = sxQuery(item);
                const $addToCart = $item.find(addToCartTrigger);
                $addToCart.on('click', () => {
                    this.trackSerpOrSuggestAddToCart(item, countSource, priceSource, priceUnitSource, articleNumberSource, identifierSource, undefined, true, getQuery());
                });
            });
            this.debugMethod('Attached add to cart events to suggest box items', sxQuery(suggestBox).find(itemSelector).find(addToCartTrigger).get());
        }
    }
    trackSerpOrSuggestAddToCart(item, countSource, priceSource, priceUnitSource, articleNumberSource, identifierSource, excludeSelector, isSuggest = false, query) {
        let link;
        // @ts-ignore
        const firstAnchor = item.querySelector(`a[href]${excludeSelector ? `:not(${excludeSelector})` : ''}`) || item;
        if (firstAnchor) {
            link = firstAnchor.getAttribute('href');
        }
        const countValue = EventHelper.getDataSourceValue(countSource, item);
        const count = countValue ? parseInt(countValue, 10) : 1;
        const priceValue = EventHelper.getDataSourceValue(priceSource, item);
        let price = parseFloat(priceValue);
        if (Helper.isNaN(price) && priceValue !== null && priceValue !== undefined) {
            price = parseFloat(priceValue.replace(/,/g, '.'));
        }
        if (Helper.isNaN(price)) {
            price = undefined;
        }
        this.tracker.trackAddToCart(EventHelper.getDataSourceValue(articleNumberSource, item), EventHelper.getDataSourceValue(identifierSource, item), link, count, price, EventHelper.getDataSourceValue(priceUnitSource, item), !isSuggest, isSuggest, false, query);
    }
    bindSerpEvents(expectedConditions, querySource, resultBlockSelector, resultSelector, excludeSelector, groupSelector, articleNumberSource, identifierSource, addToCartTrigger, countSource, priceSource, priceUnitSource, unbindAbandonSelector, rebindStrategy = [], minChangedConditions = 2) {
        // check context
        if (!EventHelper.matchesExpectedConditions(expectedConditions)) {
            this.debugMethod('Search result page conditions not met');
            return;
        }
        const query = EventHelper.getDataSourceValue(querySource);
        const $resultBlocks = sxQuery(resultBlockSelector);
        if (!$resultBlocks || $resultBlocks.length === 0) {
            this.debugMethod('No search result blocks found');
            return;
        }
        $resultBlocks.each((resultBlock) => {
            const results = removeHiddenItems(resultBlock.querySelectorAll(resultSelector));
            if (!this.isSerpShownBound || rebindStrategy.indexOf(RebindStrategy.Impression) !== -1 || (rebindStrategy.indexOf(RebindStrategy.ConditionChange) !== -1 && EventHelper.conditionsChanged(expectedConditions, this.lastExpectedConditions, minChangedConditions))) {
                this.tracker.trackSerpShow(resultBlock, results.length > 0 ? results[0] : undefined, query, results.length, undefined, undefined);
                this.isSerpShownBound = true;
                if (rebindStrategy.indexOf(RebindStrategy.ConditionChange) !== -1) {
                    this.isSerpClickBound = false;
                    this.isSerpAbandonBound = false;
                    this.lastExpectedConditions = EventHelper.getMatchingConditions(expectedConditions);
                }
            }
            const clickCallback = (e, link) => {
                this.clearEvents(sxQuery(window));
                this.disableOnUnloadFlag = true;
                const currentResults = removeHiddenItems(resultBlock.querySelectorAll(resultSelector));
                const position = Helper.getPositionInNodeList(currentResults, e.target);
                let positionInContentGroup = position;
                if (groupSelector !== undefined) {
                    const groups = [].slice.call(document.querySelectorAll(groupSelector));
                    for (let i = 0; i < groups.length; i++) {
                        const groupItems = removeHiddenItems(groups[i].querySelectorAll(resultSelector));
                        const inGroupPosition = Helper.getPositionInNodeList(groupItems, e.target);
                        if (inGroupPosition > -1) {
                            positionInContentGroup = inGroupPosition;
                            break;
                        }
                    }
                }
                if (link === undefined) {
                    // @ts-ignore
                    if (e.target.tagName === 'A') {
                        // @ts-ignore
                        link = e.target.getAttribute('href');
                    }
                    else {
                        // @ts-ignore
                        const firstAnchor = e.target.querySelector(`a[href]${excludeSelector ? `:not(${excludeSelector})` : ''}`);
                        if (firstAnchor) {
                            link = firstAnchor.getAttribute('href');
                        }
                    }
                }
                this.tracker.trackSerpClick(query, 
                // @ts-ignore
                e.target, resultBlock, currentResults.length, position, positionInContentGroup, link, undefined, 
                // @ts-ignore
                EventHelper.getDataSourceValue(articleNumberSource, e.target), 
                // @ts-ignore
                EventHelper.getDataSourceValue(identifierSource, e.target));
            };
            let addToCartAttached = false;
            if (!this.isSerpClickBound || rebindStrategy.indexOf(RebindStrategy.Click) !== -1) {
                const activeSelector = `a[href]${excludeSelector ? `:not(${excludeSelector})` : ''}`;
                this.clearEvents(sxQuery(activeSelector));
                this.clearEvents(sxQuery(resultBlock).find(resultSelector));
                if (addToCartTrigger !== undefined) {
                    this.clearEvents(sxQuery(resultBlock).find(addToCartTrigger));
                }
                results.forEach((result) => {
                    // @ts-ignore
                    const anchors = result.tagName === 'A' ? [] : [].slice.call(result.querySelectorAll(activeSelector));
                    if (anchors.length === 0) {
                        sxQuery(result).on(`click.${this.instanceId}`, () => {
                            // @ts-ignore
                            clickCallback({ target: result }, undefined);
                        });
                    }
                    else {
                        anchors.forEach((anchor) => {
                            sxQuery(anchor).on(`click.${this.instanceId}`, () => {
                                // @ts-ignore
                                clickCallback({ target: result }, anchor.getAttribute('href'));
                            });
                        });
                    }
                    if (addToCartTrigger !== undefined) {
                        const $addToCart = sxQuery(result).find(addToCartTrigger);
                        if ($addToCart.length > 0) {
                            addToCartAttached = true;
                            $addToCart.on(`click.${this.instanceId}`, () => {
                                this.trackSerpOrSuggestAddToCart(result, countSource, priceSource, priceUnitSource, articleNumberSource, identifierSource, excludeSelector, false, query);
                            });
                        }
                    }
                });
                this.debugMethod('Attached click events to search results', results);
                if (addToCartAttached) {
                    this.debugMethod('Attached add to cart events to search results', results);
                }
            }
        });
        this.isSerpClickBound = true;
        if (!this.isSerpAbandonBound || rebindStrategy.indexOf(RebindStrategy.Abandon) !== -1) {
            this.clearEvents(sxQuery(window));
            const beforeUnload = () => {
                const resultBlock = removeHiddenItems($resultBlocks.get())[0];
                const currentItems = removeHiddenItems([].slice.call(resultBlock.querySelectorAll(resultSelector)));
                if (!this.disableOnUnloadFlag) {
                    this.tracker.trackSerpLeave(resultBlock, currentItems && currentItems.length > 0 ? currentItems[0] : undefined, query, currentItems.length, undefined);
                }
            };
            if (!this.disableOnUnloadFlag) {
                sxQuery(window).on(`beforeunload.${this.instanceId}`, beforeUnload);
                this.debugMethod('Attached abandon event');
            }
        }
        if (unbindAbandonSelector !== undefined) {
            sxQuery(resultBlockSelector).find(resultSelector).find(unbindAbandonSelector).each((node) => {
                const $node = sxQuery(node);
                const handler = () => {
                    sxQuery(window).off(`beforeunload.${this.instanceId}`);
                };
                if (node.tagName === 'FORM') {
                    $node.on('submit', handler);
                }
                else {
                    $node.on('click', handler);
                }
            });
        }
    }
    bindCartEvents(config) {
        // check context
        if (!EventHelper.matchesExpectedConditions(config.expectedConditions)) {
            this.debugMethod('Cart tracking conditions not met');
            return;
        }
        const $trigger = sxQuery(config.trigger);
        this.clearEvents($trigger);
        let eventName = `click.${this.instanceId}`;
        if ($trigger.length > 0 && $trigger.get()[0].tagName === 'FORM') {
            eventName = `submit.${this.instanceId}`;
        }
        $trigger.on(eventName, () => {
            const wrapper = config.wrapper !== undefined ? document.querySelector(config.wrapper) : undefined;
            let articleNumber = EventHelper.getDataSourceValue(config.articleNumberSource, wrapper);
            if (articleNumber !== undefined && articleNumber !== null) {
                articleNumber = articleNumber.toString().trim();
            }
            const identifier = EventHelper.getDataSourceValue(config.identifierSource, wrapper);
            const link = config.linkSource ? EventHelper.getDataSourceValue(config.linkSource, wrapper) : window.location.href;
            const countValue = EventHelper.getDataSourceValue(config.countSource, wrapper);
            const count = countValue ? parseInt(countValue, 10) : 1;
            const priceValue = EventHelper.getDataSourceValue(config.priceSource, wrapper);
            let price = parseFloat(priceValue);
            if (Helper.isNaN(price) && priceValue !== null && priceValue !== undefined) {
                price = parseFloat(priceValue.replace(/,/g, '.'));
            }
            if (Helper.isNaN(price)) {
                price = undefined;
            }
            const priceUnit = EventHelper.getDataSourceValue(config.priceUnitSource, wrapper);
            this.tracker.trackAddToCart(articleNumber, identifier, link, count, price, priceUnit);
        });
        this.debugMethod('Attached PDP add to cart events to', $trigger.get());
    }
    bindCheckoutEvents(config) {
        // check context
        if (!EventHelper.matchesExpectedConditions(config.expectedConditions)) {
            this.debugMethod('Checkout tracking conditions not met');
            return;
        }
        const trackCheckout = () => {
            let data = [];
            if (config.getter !== undefined) {
                data = config.getter();
            }
            else if (config.itemSource !== undefined) {
                const { itemSource } = config;
                const $entries = sxQuery(itemSource.selector);
                $entries.get().forEach((entry) => {
                    const countValue = EventHelper.getDataSourceValue(itemSource.countSource, entry);
                    const count = countValue ? parseInt(countValue, 10) : 1;
                    const priceValue = EventHelper.getDataSourceValue(itemSource.unitPriceSource, entry);
                    let unitPrice = parseFloat(priceValue);
                    if (Helper.isNaN(unitPrice) && unitPrice !== null && unitPrice !== undefined) {
                        unitPrice = parseFloat(priceValue.replace(/,/g, '.'));
                    }
                    if (Helper.isNaN(unitPrice)) {
                        unitPrice = undefined;
                    }
                    let articleNumber = EventHelper.getDataSourceValue(itemSource.articleNumberSource, entry);
                    if (articleNumber !== undefined && articleNumber !== null) {
                        articleNumber = articleNumber.toString().trim();
                    }
                    data.push({
                        articleNumber,
                        link: EventHelper.getDataSourceValue(itemSource.linkSource, entry),
                        identifier: EventHelper.getDataSourceValue(itemSource.identifierSource, entry),
                        unitPrice,
                        priceUnit: EventHelper.getDataSourceValue(itemSource.priceUnitSource, entry),
                        count
                    });
                });
            }
            else if (config.itemObject !== undefined) {
                const { itemObject } = config;
                try {
                    //  @ts-ignore
                    const arr = itemObject.selector !== undefined ? JSON.parse(document.querySelector(itemObject.selector).innerText) : window[itemObject.windowProperty];
                    arr.forEach((obj) => {
                        const countValue = EventHelper.resolveJPath(obj, itemObject.countJPath);
                        const count = countValue ? parseInt(countValue, 10) : 1;
                        const priceValue = EventHelper.resolveJPath(obj, itemObject.unitPriceJPath);
                        let unitPrice = parseFloat(priceValue);
                        if (Helper.isNaN(unitPrice) && unitPrice !== null && unitPrice !== undefined) {
                            unitPrice = parseFloat(priceValue.replace(/,/g, '.'));
                        }
                        if (Helper.isNaN(unitPrice)) {
                            unitPrice = undefined;
                        }
                        let articleNumber = EventHelper.resolveJPath(obj, itemObject.articleNumberJPath);
                        if (articleNumber !== undefined && articleNumber !== null) {
                            articleNumber = articleNumber.toString().trim();
                        }
                        data.push({
                            articleNumber,
                            link: EventHelper.resolveJPath(obj, itemObject.linkJPath),
                            identifier: EventHelper.resolveJPath(obj, itemObject.identifierJPath),
                            unitPrice,
                            priceUnit: EventHelper.resolveJPath(obj, itemObject.priceUnitJPath),
                            count
                        });
                    });
                }
                catch (err) {
                    console.error(err);
                }
            }
            this.tracker.trackBuy(data, config.trackAll !== undefined ? config.trackAll : false);
        };
        if (config.trigger === 'pageload') {
            trackCheckout();
            this.debugMethod('Tracked checkout on pageload');
        }
        else {
            let lastTriggerNode;
            const bind = () => {
                const $trigger = sxQuery(config.trigger);
                lastTriggerNode = $trigger.get(0);
                this.clearEvents($trigger);
                let eventName = `click.${this.instanceId}`;
                if ($trigger.length > 0 && $trigger.get()[0].tagName === 'FORM') {
                    eventName = `submit.${this.instanceId}`;
                }
                $trigger.on(eventName, () => {
                    trackCheckout();
                    this.clearEvents($trigger);
                });
                this.debugMethod('Attached checkout tracking events to', $trigger.get());
            };
            bind();
            if (config._triggerCanChange) {
                setInterval(() => {
                    const $trigger = sxQuery(config.trigger);
                    if (lastTriggerNode !== $trigger.get(0)) {
                        bind();
                    }
                }, 500);
            }
        }
    }
    unbindAll() {
        this.clearEvents(sxQuery('*'));
        this.debugMethod('Detached all events');
    }
}
export default EventBinder;
